import { defineStore } from 'pinia';

import { useUserPledgesStore } from '@/Stores/Web/userPledgesStore';
import { useProjectStore } from '@/Stores/Web/projectStore';
import { useCurrencyStore } from '@/Stores/Web/currencyStore';
import { useProjectContextStore } from '@/Stores/Web/projectContextStore';

import { buildOrderSummaryModel } from '@/Components/Orders/OrderSummaryUtils';
import { buildAvailablePaymentMethods } from '@/Components/Payments/PaymentUtils';
import { methodQueue } from '@/Utils/AsyncUtility';

import { paymentMethodReasons, orderStages, paymentTypes, paymentProvider as paymentProviders } from '@/Models.js';

import Resources from '@/Resources.js';
import Formatter from '@/Utils/Formatter';

import { apiClient } from '@/Clients/ApiClient';
import CartsService from '@/Services/CartsService';
import OrdersService from '@/Services/OrdersService';
import ShippingService from '@/Services/ShippingService';
import OrderShippingService from '@/Services/OrderShippingService';
import PledgeService from '@/Services/PledgeService';

import { defaultCartModel } from '@/Components/Orders/OrderUtility.js';

const shippingService = new ShippingService(apiClient);
const orderShippingService = new OrderShippingService(apiClient);

const pledgeService = new PledgeService(apiClient);
const cartsService = new CartsService(apiClient);
const ordersService = new OrdersService(apiClient);

const paymentMethodsRequests = {};

export const useOrderStore = defineStore({
    id: 'order',
    state: () => ({
        orderPaymentType: null,
        projectLocations: [],
        shippingModels: [],
        shippingModel: null,
        isLocationSelected: false,
        orderSummaryListModel: {},
        currentOrderId: 0,
        orderDetails: {},
        selectedPaymentMethod: null,
        availablePaymentMethods: [],
        disabledPaymentMethods: [],
        isOrderDataInitialized: false,
    }),
    actions: {
        setOrderDetails(order) {
            const { orderID } = order.data.cart || order.data.order;
            this.currentOrderId = orderID;
            this.orderDetails = { ...this.orderDetails, [orderID]: order.data };
            this.isOrderDataInitialized = true;
        },
        async getCartDetails(model = this.orderDetailsRequestModel) {
            this.orderDetailsRequestModel = model;
            try {
                const { data } = await cartsService.getCartDetails(this.orderDetailsRequestModel);
                this.setOrderDetails(data);
                return data;
            }
            catch (error) {
                throw error.message;
            }
        },
        async getOrderDetails(model = this.orderDetailsRequestModel) {
            this.orderDetailsRequestModel = model;
            try {
                const { data } = await ordersService.getOrderDetails(this.orderDetailsRequestModel);
                this.setOrderDetails(data);
                return data;
            }
            catch (error) {
                throw error.message;
            }
        },
        async getManageShippingOrderDetails(model = this.orderDetailsRequestModel) {
            this.orderDetailsRequestModel = model;
            try {
                const { data } = await ordersService.getManageShippingOrderDetails(this.orderDetailsRequestModel);
                this.setOrderDetails(data);
                return data;
            }
            catch (error) {
                throw error.message;
            }
        },
        async getAwaitingPaymentOrderDetails(model = this.orderDetailsRequestModel) {
            this.orderDetailsRequestModel = model;
            try {
                const { data } = await ordersService.getAwaitingPaymentOrderDetails(this.orderDetailsRequestModel);
                this.setOrderDetails(data);
                return data;
            }
            catch (error) {
                throw error.message;
            }
        },
        async setOrderSummaryListModel(orderId) {
            const userPledgesStore = useUserPledgesStore();
            const projectStore = useProjectStore();
            const { projectID } = useProjectContextStore();
            const locations = await projectStore.getProjectLocations(projectID);

            const orderDetails = orderId ? this.getOrderById(orderId) : this.currentOrderDetails;
            const { orderID } = orderDetails.cart || orderDetails.order;
            this.orderSummaryListModel = {
                ...this.orderSummaryListModel,
                [orderID]: buildOrderSummaryModel(
                    orderDetails,
                    userPledgesStore.firstPledge,
                    locations,
                ),
            };
        },
        async updateOrderShippingModel(shippingModelID = this.shippingModel) {
            const { projectID } = useProjectContextStore();
            try {
                await this.setShippingModel(projectID, shippingModelID);
            }
            catch (error) {
                throw new Error(error);
            }
        },
        // Locations
        setLocationStatus(status) {
            this.isLocationSelected = status;
        },
        async onOrderLocationChanged(model = this.orderDetailsRequestModel, customerLocationID, customerSubLocationID) {
            const { hasEarlyShippingCollectionEnabled } = useProjectContextStore();

            if (hasEarlyShippingCollectionEnabled) {
                await this.loadShippingModels(customerLocationID, customerSubLocationID);
                await this.updateOrderShippingModel();
            }
            await this.getCartDetails(model);
            await this.setOrderSummaryListModel();
        },
        // Shipping methods
        async loadShippingModels(customerLocationID = null, customerSubLocationID = null) {
            const { projectID } = useProjectContextStore();
            const {
                shippingModelID,
                orderCode,
            } = this.currentOrderDetails.cart || this.currentOrderDetails.order;
            let {
                customerProjectLocationID,
                customerProjectSubLocationID,
            } = this.currentOrderDetails.cart || this.currentOrderDetails.order;
            if ( customerLocationID || customerSubLocationID) {
                customerProjectLocationID = customerLocationID;
                customerProjectSubLocationID = customerSubLocationID;
            }
            try {
                const { data } = await shippingService.getEnabledShippingModels(
                    projectID,
                    customerProjectLocationID,
                    customerProjectSubLocationID,
                    orderCode,
                );
                this.shippingModels = data;
                const isShippingModelNotSelected = !shippingModelID || !this.shippingModels.find(m => m.shippingModelID === shippingModelID);
                if (isShippingModelNotSelected) {
                    const selectedModel = this.shippingModels.find(m => m.isDefault) || this.shippingModels[0];
                    if (!selectedModel) {
                        throw Resources.Checkout.ShippingModelsNotAvailableErrorMessage;
                    }
                    this.updateShippingModelID(selectedModel.shippingModelID);
                }
                else {
                    this.updateShippingModelID(shippingModelID);
                }
            }
            catch (error) {
                this.resetShippingModelData();
                throw new Error(error);
            }
        },
        resetShippingModelData() {
            this.shippingModel = null;
            this.shippingModels = [];
        },
        updateShippingModelID(shippingModelID) {
            this.shippingModel = shippingModelID;
        },
        async setShippingModel(projectID, shippingModel) {
            if (this.isLocationSelected) {
                try {
                    await orderShippingService.saveCartShippingModel(projectID, shippingModel);
                }
                catch (error) {
                    throw new Error(error);
                }
            }
        },
        // Payments
        async getAvailablePaymentMethods(locationIsoCode = this.locationIsoCode, reason = paymentMethodReasons.unknown, projectID) {
            const { paymentProvider } = useProjectContextStore();
            const isAdyenPaymentProvider = paymentProvider === paymentProviders.adyenV1 || paymentProvider === paymentProviders.adyenV2;
            if (!isAdyenPaymentProvider) {
                return;
            }
            const hasOrderCode = this.currentOrderDetails?.cart?.orderStage !== orderStages.unfinished || this.currentOrderDetails?.cart?.hasParent;
            await methodQueue.enqueue(async () => {
                const { data } = await this.loadAvailablePaymentMethods({
                    currencyShortName: this.currencyShortName,
                    projectID,
                    locationIsoCode,
                    orderCode: hasOrderCode ? this.currentOrderDetails?.cart.orderCode : null,
                    reason,
                });
                this.setAvailablePaymentMethods(buildAvailablePaymentMethods(data, this.disabledPaymentMethods));
            });
        },
        setAvailablePaymentMethods(paymentMethods) {
            // If the selected payment method is not available, reset it.
            if (paymentMethods.length === 1) {
                this.setSelectedPaymentMethod(paymentMethods[0].paymentDataSource);
            }
            else if (!paymentMethods.some(pm => pm.paymentDataSource === this.selectedPaymentMethod)) {
                this.setSelectedPaymentMethod(null);
            }

            this.availablePaymentMethods = paymentMethods;
        },
        async loadAvailablePaymentMethods(params) {
            const paramsKey = JSON.stringify(params);
            // If the payment methods are already loaded, return them.
            if (paymentMethodsRequests[paramsKey]) {
                return await paymentMethodsRequests[paramsKey];
            }
            const requestPromise = pledgeService.getAvailablePaymentMethods(params);
            paymentMethodsRequests[paramsKey] = requestPromise;
            return await requestPromise;
        },
        setSelectedPaymentMethod(paymentDataSource) {
            this.selectedPaymentMethod = paymentDataSource;
        },
        setDisabledPaymentMethods(disabledPaymentMethods = []) {
            this.disabledPaymentMethods = disabledPaymentMethods;
        },
        setOrderPaymentType(paymentType) {
            this.orderPaymentType = paymentType;
        },
    },
    getters: {
        getOrderById: state => (orderID = this.currentOrderId) => {
            // TODO: Fix the use cases where we use cartDetails.cart in our components to also support orderDetails.order
            // https://dev.azure.com/gamefound/Gamefound/_workitems/edit/20223
            const orderDetails = state.orderDetails[orderID] || defaultCartModel;
            orderDetails.cart = orderDetails.cart || orderDetails.order;
            return orderDetails;
        },
        shippingModelsLength: state => state.shippingModels.length,
        currentOrderDetails: state => {
            // TODO: Fix the use cases where we use cartDetails.cart in our components to also support orderDetails.order
            // https://dev.azure.com/gamefound/Gamefound/_workitems/edit/20223
            const orderDetails = state.orderDetails[state.currentOrderId] || defaultCartModel;
            orderDetails.cart = orderDetails.cart || orderDetails.order;
            return orderDetails;
        },
        currencyShortName() {
            const currencyStore = useCurrencyStore();
            const selectedDccCurrency = currencyStore.selectedCurrency;

            return !this.currentOrderDetails
                ? null
                : selectedDccCurrency
                    ? selectedDccCurrency.shortName
                    : this.currentOrderDetails?.cart.currencyShortName;
        },
        locationIsoCode() {
            return this.currentOrderDetails
                ? this.currentOrderDetails?.cart.shippingProjectLocationIsoCode ?? this.currentOrderDetails?.cart.customerProjectLocationIsoCode
                : null;
        },
        orderAmount() {
            const currencyStore = useCurrencyStore();
            const selectedDccCurrency = currencyStore.selectedCurrency;

            return !this.currentOrderDetails
                ? null
                : selectedDccCurrency
                    ? currencyStore.selectedCurrency.amountConverted
                    : this.currentOrderDetails?.cart.effectivePayableAmount;

        },
        originalCurrencyOrderAmount() {
            return this.currentOrderDetails?.cart.effectivePayableAmount;
        },
        orderAmountWithSymbol() {
            const currencyStore = useCurrencyStore();
            const selectedDccCurrency = currencyStore.selectedCurrency;

            return !this.currentOrderDetails
                ? null
                : Formatter.format(this.orderAmount, 'C2', selectedDccCurrency?.symbol ?? this.currentOrderDetails?.cart.currencySymbol);
        },
        hasOrderRestrictedItems() {
            return this.currentOrderDetails?.orderItemIDsRestrictedInCartLocation?.length > 0;
        },
        restrictedItems() {
            return this.currentOrderDetails?.orderItemIDsRestrictedInCartLocation || [];
        },
        paymentMethodsByOrderPaymentType() {
            return this.orderPaymentType === paymentTypes.installment
                ? paymentMethodReasons.checkoutWithInstallments
                : paymentMethodReasons.checkout;
        },
        handleTax() {
            return this.currentOrderDetails?.cart.handleTax;
        },
        isOrderValid() {
            return this.currentOrderDetails?.productOrderItems?.every(item => item.validationResult.isValid);
        },
        getOrderCurrencySymbol: state => (orderID) => {
            const order = state.orderDetails[orderID];
            return order?.cart?.currencySymbol;
        },
    },
});